import { SerializedRequest, SerializedResponse } from '../../serialize'
import type { ApiRoutePath } from '../../../dist/apiRoutePaths'
import { isSuccessResponse } from '../../../helpers/isSuccessResponse'

const target = new EventTarget()
const EVENT_NAME = 'micro-frontend-fetch-response'
type HttpMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'PATCH'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS'

const getPathname = (url: string): string => {
  try {
    return new URL(url, 'https://x').pathname
  } catch {
    return url
  }
}

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const routePathToRegex = (routePath: ApiRoutePath): RegExp => {
  const pattern = routePath
    .split(/(:[^/]+)/)
    .map((part) => (part.startsWith(':') ? '[^/]+' : escapeRegex(part)))
    .join('')
  return new RegExp('^' + pattern + '$')
}

const matchesRoute = (responseUrl: string, routePath: ApiRoutePath): boolean =>
  routePathToRegex(routePath).test(getPathname(responseUrl))

const matchesUrl = (
  responseUrl: string,
  routePathOrPaths: ApiRoutePath | ApiRoutePath[],
): boolean =>
  Array.isArray(routePathOrPaths)
    ? routePathOrPaths.some((p) => matchesRoute(responseUrl, p))
    : matchesRoute(responseUrl, routePathOrPaths)

export type FetchInterceptorOptions<R = unknown, A = unknown> = {
  url: ApiRoutePath | ApiRoutePath[]
  method?: HttpMethod | HttpMethod[]
  callback: (args: {
    request: Omit<SerializedRequest, 'body'> & { body: A }
    response: Omit<SerializedResponse, 'body'> & { body: R }
    method?: HttpMethod
  }) => void
  withError?: boolean
}

export const on = <R = unknown, A = unknown>(
  options: FetchInterceptorOptions<R, A>,
) => {
  const { url, method: methodOpt, callback, withError = false } = options

  const eventListener = (event: Event) => {
    const customEvent = event as CustomEvent<{
      request: SerializedRequest
      response: SerializedResponse
    }>

    const { request, response } = customEvent.detail

    if (!matchesUrl(response.url, url)) return

    const requestMethod = request.method?.toUpperCase() as
      | HttpMethod
      | undefined

    if (methodOpt !== undefined) {
      const allowed = (Array.isArray(methodOpt) ? methodOpt : [methodOpt]).map(
        (m) => m.toUpperCase(),
      )
      if (!requestMethod || !allowed.includes(requestMethod)) return
    }

    const success = isSuccessResponse(response)
    if (!withError && !success) return

    let requestBody: A
    if (request.body) {
      try {
        requestBody = JSON.parse(request.body) as A
      } catch {
        requestBody = request.body as A
      }
    } else {
      requestBody = null as A
    }
    const responseBody = response.body as R

    callback({
      request: { ...request, body: requestBody },
      response: { ...response, body: responseBody },
      method: requestMethod,
    })
  }

  target.addEventListener(EVENT_NAME, eventListener)
  return () => target.removeEventListener(EVENT_NAME, eventListener)
}

export const emit = (
  request: SerializedRequest,
  response: SerializedResponse,
) => {
  target.dispatchEvent(
    new CustomEvent(EVENT_NAME, {
      detail: { request, response },
    }),
  )
}
